import { useState } from "react";
import { useEffect } from "react";
import { Fragment } from "react";
import { connect } from "react-redux";

import EditIcon from "@mui/icons-material/Edit";
import TrendingDown from "@mui/icons-material/TrendingDown";
import TrendingFlat from "@mui/icons-material/TrendingFlat";
import TrendingUp from "@mui/icons-material/TrendingUp";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { alpha, emphasize, useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import * as PatientSessionActions from "../../state/redux/patientSession";
import { capitalizeAllWords, deepCopyObject } from "../../utils/helpers";
import PatientGoalsDialog from "../dialogs/patientGoalsDialog";
import SingleGoalDialog from "../dialogs/singleGoalDialog";
import { extractGraphDataFromArray } from "../graphing/graphUtils";
import OmniAvatar from "../ui/omniAvatar";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget";
import WidgetList from "../ui/widgetList";
import WidgetSkeleton from "../ui/widgetSkeleton";
import WidgetTitle from "../ui/widgetTitle";

const statistical = require("statistical-js");

const PatientGoals = props => {
  const { patientODM, patientChart, title = "smart Goals", allowAdd = true } = props;

  const [showInputDialog, setShowInputDialog] = useState(false);

  const [singleGoalData, setSingleGoalData] = useState(null);

  const generateGoalData = () => {
    const userInfo = patientChart;
    if (userInfo == null) {
      return null;
    }

    const singleGoalsData = [];
    for (const [key] of Object.entries(userInfo.goals)) {
      let graphData = extractGraphDataFromArray(userInfo.goals[key].data);
      let linearInput = [];
      for (let x = 0; x < graphData.values.length; ++x) {
        linearInput.push([x, graphData.values[x]]);
      }
      //this takes an array [x,y] values
      const linearOutput = statistical.methods.linearRegression(linearInput);
      const average = statistical.methods.mean(graphData.values);

      singleGoalsData.push({
        name: capitalizeAllWords(key.replace(/([A-Z])/g, " $1").trim()),
        averageSeverity: Math.round(average),
        regressionValue: linearOutput,
        graphDataComplete: graphData.values,
        graphLabels: graphData.labels,
        graphIndex: 0,
        goalId: key,
      });
    }

    singleGoalsData.sort(
      (a, b) => b.regressionValue.slope.toFixed(2) - a.regressionValue.slope.toFixed(2),
    );

    return singleGoalsData;
  };

  const getRegressionDescription = regressionValue => {
    const toFixed = regressionValue.slope.toFixed(2);
    if (toFixed > 0.01) {
      return "Increasing";
    }
    if (toFixed < -0.01) {
      return "Decreasing";
    } else {
      return "Steady";
    }
  };

  const getRegressionIcon = regressionValue => {
    const toFixed = regressionValue.slope.toFixed(2);
    if (toFixed > 0.01) {
      return (
        <TrendingUp
          sx={{
            color: theme.palette.info.contrastText,
            width: 18,
            height: 18,
            marginLeft: "2px",
            marginTop: "1px",
            fontSize: "1.0rem",
          }}
        />
      );
    }
    if (toFixed < -0.01) {
      return (
        <TrendingDown
          sx={{
            color: theme.palette.info.contrastText,
            width: 18,
            height: 18,
            marginLeft: "2px",
            marginTop: "1px",
            fontSize: "1.0rem",
          }}
        />
      );
    } else {
      return (
        <TrendingFlat
          sx={{
            color: theme.palette.info.contrastText,
            width: 18,
            height: 18,
            marginLeft: "2px",
            marginTop: "1px",
            fontSize: "1.0rem",
          }}
        />
      );
    }
  };

  const [goalData, setGoalData] = useState(generateGoalData());

  useEffect(() => {
    setGoalData(generateGoalData());
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientChart]);

  const [titleText, setTitleText] = useState(null);

  useEffect(() => {
    if (goalData != null) {
      setTitleText(`Tracking ${goalData.length} Goal${goalData.length === 1 ? "" : "s"}`);
    } else setTitleText("");
  }, [goalData]);

  const theme = useTheme();

  return (
    <Widget>
      <WidgetTitle
        title={title}
        text={titleText}
        loading={patientODM === null || patientChart === null}
      >
        {allowAdd && (
          <OmniResponsiveButton
            tooltipTitle="Update Tracking"
            buttonText="Update"
            icon={<EditIcon />}
            onClick={() => {
              setShowInputDialog(true);
            }}
          />
        )}
      </WidgetTitle>

      {(patientODM == null || patientChart == null) && <WidgetSkeleton />}
      {patientODM != null && patientChart != null && (
        <WidgetList>
          {goalData != null &&
            goalData.map((goal, index) => {
              return (
                <ListItem
                  dense
                  key={index}
                  disableGutters={true}
                  onClick={() => {
                    setSingleGoalData(goal);
                  }}
                  sx={{
                    paddingRight: theme.spacing(1),
                    paddingLeft: theme.spacing(1),
                    paddingTop: 0,
                    paddingBottom: 0,
                    marginBottom: theme.spacing(1),
                    borderRadius: theme.spacing(1),
                    cursor: "pointer",
                    backgroundColor: alpha(theme.palette.info.main, 0.75),
                    color: theme.palette.info.contrastText,
                    boxShadow: theme.shadows[3],
                    "&:hover": {
                      backgroundColor: emphasize(theme.palette.info.main, 0.5),
                      cursor: "pointer",
                    },
                  }}
                >
                  <OmniAvatar
                    sx={[
                      {
                        color: theme.palette.info.main,
                        backgroundColor: theme.palette.info.contrastText,
                        width: 44,
                        height: 44,
                        fontSize: "2rem",
                      },
                      goal.averageSeverity >= 100 && {
                        color: theme.palette.info.main,
                        backgroundColor: theme.palette.info.contrastText,
                        width: 44,
                        height: 44,
                        fontSize: "0.8rem",
                        fontWeight: 600,
                      },
                    ]}
                  >
                    {goal.averageSeverity}
                  </OmniAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        component="span"
                        variant="h6"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          marginLeft: theme.spacing(1),
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: theme.palette.info.contrastText,
                          marginTop: 0,
                          webkitUserSelect: "none",
                          userSelect: "none",
                        }}
                      >
                        {goal.name}
                      </Typography>
                    }
                    secondary={
                      <Fragment>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            marginLeft: theme.spacing(1),
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            marginTop: "-6px",
                            color: theme.palette.info.contrastText,
                            webkitUserSelect: "none",
                            userSelect: "none",
                          }}
                        >
                          {getRegressionDescription(goal.regressionValue)}
                          {getRegressionIcon(goal.regressionValue)}
                        </Typography>
                      </Fragment>
                    }
                  />
                </ListItem>
              );
            })}
        </WidgetList>
      )}

      {showInputDialog && (
        <PatientGoalsDialog
          open={goalData != null}
          goalData={goalData}
          onAccept={updatedGoals => {
            //TODO update them
            //update the PatientChart with the new data

            let today = new Date();
            today = today.toISOString().substring(0, 10);
            let newGoals = deepCopyObject(patientChart.goals);

            for (let index = 0; index < updatedGoals.length; ++index) {
              let theNewEntry = {
                date: today,
                value: updatedGoals[index].newReportedValue,
                note: "",
              };
              newGoals[updatedGoals[index].goalId].data.push(theNewEntry);
            }

            props.updatePatientGoalsRequest(newGoals);
            setShowInputDialog(false);
          }}
          onClose={() => {
            setShowInputDialog(false);
          }}
        />
      )}

      {singleGoalData != null && (
        <SingleGoalDialog
          open={singleGoalData != null}
          onAccept={() => {
            setSingleGoalData(null);
          }}
          onClose={() => {
            setSingleGoalData(null);
          }}
          dialogTitle={`${singleGoalData.name} History`}
          singleGoalData={singleGoalData}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = state => ({
  patientODM: state.patientSession.patientODM,
  patientChart: state.patientSession.patientChart,
});

const mapDispatchToProps = {
  updatePatientGoalsRequest: PatientSessionActions.updatePatientGoalsRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(PatientGoals);
